import { useEffect, useRef, useState } from "react";
import { UploadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadImage, formatApiError } from "@/lib/cms";

export const ImageField = ({ value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL or upload"
          className="flex-1 rounded-lg border-2 border-input bg-background px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="flex items-center gap-2 rounded-lg border-2 border-border px-3 py-2 text-xs font-bold uppercase tracking-wide text-foreground hover:border-primary"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
          Upload
        </button>
        <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
      </div>
      {value && (
        <img src={value} alt="preview" className="h-20 w-auto rounded-lg border border-border bg-white object-contain p-1" />
      )}
    </div>
  );
};

const FIELD_BASE = "w-full rounded-lg border-2 border-input bg-background px-3 py-2 text-sm";

const TagsField = ({ value, onChange, placeholder }) => {
  const [text, setText] = useState(Array.isArray(value) ? value.join("\n") : "");
  useEffect(() => {
    setText(Array.isArray(value) ? value.join("\n") : "");
  }, [value]);
  return (
    <textarea
      rows={3}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onChange(text.split("\n").map((s) => s.trim()).filter(Boolean))}
      placeholder={placeholder}
      className={FIELD_BASE}
    />
  );
};

const ParagraphsField = ({ value, onChange, placeholder }) => {
  const toText = (v) => {
    if (Array.isArray(v)) return v.join("\n");
    if (typeof v === "string") return v;
    return "";
  };
  const splitParas = (raw) =>
    raw.split(/\n+/).map((s) => s.trim()).filter(Boolean);
  const [text, setText] = useState(toText(value));
  useEffect(() => {
    setText(toText(value));
  }, [value]);
  return (
    <textarea
      rows={5}
      value={text}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        onChange(splitParas(next));
      }}
      onBlur={() => onChange(splitParas(text))}
      placeholder={placeholder || "One paragraph per line"}
      className={FIELD_BASE}
    />
  );
};

export const FieldInput = ({ field, value, onChange }) => {
  const base = FIELD_BASE;
  const { type, options } = field;

  if (type === "textarea")
    return <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} />;
  if (type === "number")
    return <input type="number" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))} className={base} />;
  if (type === "bool")
    return (
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" />
        Enabled
      </label>
    );
  if (type === "select")
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={base}>
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  if (type === "image") return <ImageField value={value} onChange={onChange} />;
  if (type === "tags")
    return <TagsField value={value} onChange={onChange} placeholder="One item per line" />;
  if (type === "paragraphs")
    return <ParagraphsField value={value} onChange={onChange} placeholder="One paragraph per line" />;
  if (type === "keyvalue") {
    const entries = Object.entries(value || {});
    const update = (idx, k, v) => {
      const next = entries.map(([ek, ev], i) => (i === idx ? [k, v] : [ek, ev]));
      onChange(Object.fromEntries(next.filter(([kk]) => kk)));
    };
    return (
      <div className="space-y-2">
        {entries.map(([k, v], i) => (
          <div key={i} className="flex gap-2">
            <input value={k} onChange={(e) => update(i, e.target.value, v)} placeholder="Label" className={base} />
            <input value={v} onChange={(e) => update(i, k, e.target.value)} placeholder="Value" className={base} />
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange({ ...(value || {}), "": "" })}
          className="text-xs font-bold uppercase tracking-wide text-primary"
        >
          + Add row
        </button>
      </div>
    );
  }
  return <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} />;
};
